import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  GetManagedPlansData,
  getManagedPlansOptions,
} from '@cfa/pathway-fe-shared/xp';

import { useAuth } from '@/hooks/useAuth';
import { addSortFields } from '@/hooks/useSortOptions';
import useSystemPlans from '@/hooks/useSystemPlans';

import { sourceDataFamily } from '@/atoms/data-view';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

interface UseGetManagePlansDataProps {
  /**
   * The status of the plans to fetch.
   *
   * For Active/Archived plans.
   *
   * @default 'ACTIVE'
   */
  plansStatus?: NonNullable<GetManagedPlansData['query']>['status'];
}

const useGetManagePlansData = (
  { plansStatus = 'ACTIVE' }: UseGetManagePlansDataProps = {
    plansStatus: 'ACTIVE',
  },
) => {
  const { data } = useAuth();
  const isLicensee = data?.isLicensee;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const { systemPlans } = useSystemPlans();
  const setSource = useSetAtom(sourceDataFamily('manage-plans'));

  const {
    data: managedPlans = [],
    isFetching: isFetchingManagedPlans,
    isPending: isPendingManagedPlans,
    error: managedPlansDataError,
    refetch: refetchManagedPlans,
  } = useQuery(getManagedPlansOptions({ query: { status: plansStatus } }));

  const isFetchingPlans = isFetchingManagedPlans || isPendingManagedPlans;

  const plans = useMemo(
    () =>
      isLicensee
        ? managedPlans.filter(
            plan => plan.ownerId === activeLicenseeLocation?.number,
          )
        : managedPlans,
    [isLicensee, managedPlans, activeLicenseeLocation],
  );

  // Set Plan[] (with sort/search fields) as source data
  useEffect(() => {
    if (!isFetchingPlans) {
      setSource(
        plans.map(plan => ({
          ...addSortFields(plan),
          isReadonlySubscribed: systemPlans.some(
            sp => sp.readonly && sp.id === plan.id,
          ),
        })),
      );
    }
  }, [isFetchingPlans, plans, setSource, systemPlans]);

  return {
    plans,
    refetchPlans: refetchManagedPlans,
    isFetchingPlans,
    plansDataError: managedPlansDataError,
  };
};

export default useGetManagePlansData;
